import { motion } from 'motion/react';
import Image, { ImageProps } from 'next/image';
import { type JSX } from 'react';

export type CardState = {
    x: string;
    y: string;
    rotate: number;
    scale?: number;
};

export interface Card extends ImageProps {
    side: -1 | 0 | 1;
    width: number;
    height: number;
    top: number;
    axisOffset: number;
    zIndex: number;
    resting: CardState;
    hover: CardState;
}

type Frame = { width: number; height: number };

function toPercent(value: number, base: number): string {
    return `${(value / base) * 100}%`;
}
export interface CardLayerProps {
    card: Card;
    isHovered: boolean;
    frame: Frame;
}

export function CardLayer({ card, isHovered, frame, ...imageProps }: CardLayerProps): JSX.Element {
    const { src, alt, width, height, top, side, axisOffset, zIndex, ...rest } = card;
    // (frame center) + (signed offset from axis) − (half the card width)
    const left = frame.width / 2 + side * axisOffset - width / 2;

    return (
        <motion.div
            className="absolute origin-[50%_80%]"
            style={{
                left: toPercent(left, frame.width),
                top: toPercent(top, frame.height),
                width: toPercent(width, frame.width),
                height: toPercent(height, frame.height),
                zIndex,
            }}
            initial={false}
            animate={isHovered ? card.hover : card.resting}
            transition={{ type: 'spring', stiffness: 220, damping: 18, mass: 0.8 }}
        >
            <div className="relative size-full rounded-[14px] overflow-hidden">
                <Image
                    src={src}
                    alt={alt}
                    fill
                    sizes="320px"
                    priority
                    className="object-cover"
                    draggable={false}
                    {...rest}
                />
            </div>
        </motion.div>
    );
}
